import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2, Car, Search, Package } from "lucide-react";
import { api, money, garageVatRate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CustomerPicker } from "@/components/CustomerPicker";
import { PartPicker } from "@/components/PartPicker";
import { NumberPlate } from "@/components/NumberPlate";
import { toast } from "sonner";

export default function QuotationNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const jobId = params.get("job_id");
  const [customer, setCustomer] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [title, setTitle] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [assessmentNotes, setAssessmentNotes] = useState("");
  const [importantNote, setImportantNote] = useState("");
  const [mileage, setMileage] = useState("");
  const [category, setCategory] = useState("");
  const [catGroups, setCatGroups] = useState([]);
  const [garage, setGarage] = useState(null);
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: 0, kind: "labour" }]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Inline add-vehicle
  const [addingVeh, setAddingVeh] = useState(false);
  const [vrm, setVrm] = useState("");
  const [vehForm, setVehForm] = useState({ make: "", model: "", year: "", colour: "", fuel_type: "" });
  const [looking, setLooking] = useState(false);
  const [vehSaving, setVehSaving] = useState(false);

  useEffect(() => { api("/vehicles").then(setVehicles).catch(() => {}); }, []);
  useEffect(() => { api("/jobs/categories").then((d) => setCatGroups(d.groups || [])).catch(() => {}); }, []);
  useEffect(() => { api("/garage/settings").then(setGarage).catch(() => {}); }, []);

  // Pre-fill from a linked job (contextual "Create Quotation" from Job Details)
  useEffect(() => {
    if (!jobId) return;
    (async () => {
      try {
        const j = await api(`/jobs/${jobId}`);
        if (j.customer) setCustomer(j.customer);
        if (j.vehicle_id) setVehicleId(j.vehicle_id);
        if (j.title) setTitle(j.title);
        if (j.mileage_in != null) setMileage(String(j.mileage_in));
        if (j.category) setCategory(j.category);
      } catch { /* ignore */ }
    })();
  }, [jobId]);

  // Vehicles that belong to the chosen customer
  const customerVehicles = useMemo(
    () => (customer ? vehicles.filter((v) => v.customer_id === customer.id) : []),
    [vehicles, customer]
  );

  // When switching customer, reset vehicle selection
  const onCustomer = (c) => { setCustomer(c); setVehicleId(""); setAddingVeh(false); };

  const lookup = async () => {
    if (!vrm.trim()) return;
    setLooking(true);
    try {
      const d = await api(`/dvla/lookup/${encodeURIComponent(vrm.trim())}`);
      setVehForm({ make: d.make || "", model: d.model || "", year: d.year || "", colour: d.colour || "", fuel_type: d.fuel_type || "" });
      toast.success("DVLA details loaded");
    } catch (e) { toast.error(e.message || "Vehicle not found"); } finally { setLooking(false); }
  };

  const saveVehicle = async () => {
    if (!vrm.trim() || !vehForm.make.trim() || !vehForm.model.trim()) { toast.error("VRM, make and model are required"); return; }
    setVehSaving(true);
    try {
      const v = await api("/vehicles", { method: "POST", body: JSON.stringify({
        vrm: vrm.trim(), make: vehForm.make.trim(), model: vehForm.model.trim(),
        year: vehForm.year ? Number(vehForm.year) : null, colour: vehForm.colour || null, fuel_type: vehForm.fuel_type || null,
        customer_id: customer.id,
      }) });
      setVehicles((p) => [v, ...p]);
      setVehicleId(v.id);
      setAddingVeh(false); setVrm(""); setVehForm({ make: "", model: "", year: "", colour: "", fuel_type: "" });
      toast.success("Vehicle added");
    } catch (e) { toast.error(e.message || "Could not add vehicle"); } finally { setVehSaving(false); }
  };

  const net = items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const vatPct = garageVatRate(garage);
  const vat = net * (vatPct / 100);
  const upd = (i, k, v) => { const c = [...items]; c[i] = { ...c[i], [k]: v }; setItems(c); };

  const submit = async (e) => {
    e.preventDefault();
    if (!customer) { toast.error("Select or add a customer"); return; }
    if (!vehicleId) { toast.error("Select or add a vehicle"); return; }
    if (!category) { toast.error("Work category is required"); return; }
    const catLabel = (() => {
      for (const g of catGroups) {
        const c = (g.categories || g.items || []).find((x) => x.id === category);
        if (c) return c.label || c.name;
      }
      return title || category;
    })();
    setSaving(true);
    try {
      const q = await api("/quotations", { method: "POST", body: JSON.stringify({
        vehicle_id: vehicleId, customer_id: customer.id, title: catLabel,
        valid_until: validUntil || null, items: items.filter((i) => i.description.trim()).map((i) => ({ ...i, qty: Number(i.qty) || 0, unit_price: Number(i.unit_price) || 0 })),
        assessment_notes: assessmentNotes.trim() || "", important_note: importantNote.trim() || "",
        mileage: mileage ? Number(mileage) : null, category: category || null,
        job_id: jobId || null,
      }) });
      toast.success("Quotation created");
      navigate(`/quotations/${q.id}`);
    } catch (e2) { toast.error(e2.message); } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/quotations" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-2"><ArrowLeft className="h-4 w-4" />Quotations</Link>
      <PageHeader title="New Quotation" />
      <form onSubmit={submit} className="space-y-5 rounded-xl border border-border bg-card p-6">
        {/* Customer */}
        <div>
          <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">CUSTOMER *</span>
          <CustomerPicker value={customer} onChange={onCustomer} />
        </div>

        {/* Vehicle (only once a customer is chosen) */}
        {customer && (
          <div data-testid="quote-vehicle-section">
            <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">VEHICLE *</span>
            {!addingVeh ? (
              <div className="space-y-2">
                {customerVehicles.length > 0 ? (
                  <div className="space-y-2">
                    {customerVehicles.map((v) => (
                      <button key={v.id} type="button" onClick={() => setVehicleId(v.id)} data-testid={`quote-veh-${v.id}`}
                        className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left ${vehicleId === v.id ? "border-brand bg-brand-tertiary" : "border-border bg-card hover:bg-secondary"}`}>
                        <NumberPlate vrm={v.vrm} size="sm" />
                        <span className="text-sm font-semibold text-midnight">{v.make} {v.model}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="rounded-lg bg-secondary px-3 py-2.5 text-sm text-muted-foreground">No vehicles for this customer yet.</p>
                )}
                <button type="button" onClick={() => setAddingVeh(true)} data-testid="quote-add-vehicle-toggle"
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-tertiary px-3 py-2 text-sm font-bold text-brand-2"><Car className="h-4 w-4" />Add vehicle</button>
              </div>
            ) : (
              <div className="space-y-2 rounded-lg border border-border p-3">
                <div className="flex gap-2">
                  <input value={vrm} onChange={(e) => setVrm(e.target.value.toUpperCase())} placeholder="AB12 XYZ" data-testid="quote-new-veh-vrm"
                    className="min-w-0 flex-1 rounded-md border border-input bg-[#FCD34D] px-3 py-2.5 text-center text-lg font-extrabold tracking-widest text-black outline-none" />
                  <button type="button" onClick={lookup} disabled={looking} data-testid="quote-new-veh-lookup"
                    className="inline-flex items-center gap-1 rounded-md bg-midnight px-3 py-2 text-sm font-bold text-white disabled:opacity-60">{looking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Lookup</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={vehForm.make} onChange={(e) => setVehForm({ ...vehForm, make: e.target.value })} placeholder="Make *" data-testid="quote-new-veh-make" className="rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
                  <input value={vehForm.model} onChange={(e) => setVehForm({ ...vehForm, model: e.target.value })} placeholder="Model *" data-testid="quote-new-veh-model" className="rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
                  <input value={vehForm.year} onChange={(e) => setVehForm({ ...vehForm, year: e.target.value })} placeholder="Year" className="rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
                  <input value={vehForm.colour} onChange={(e) => setVehForm({ ...vehForm, colour: e.target.value })} placeholder="Colour" className="rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={saveVehicle} disabled={vehSaving} data-testid="quote-new-veh-save" className="flex flex-1 items-center justify-center gap-1 rounded-md bg-brand py-2 text-sm font-bold text-white disabled:opacity-60">{vehSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Save vehicle</button>
                  <button type="button" onClick={() => setAddingVeh(false)} className="rounded-md border border-border px-3 py-2 text-sm font-semibold">Back</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div>
          <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">ASSESSMENT NOTES</span>
          <textarea value={assessmentNotes} onChange={(e) => setAssessmentNotes(e.target.value)} data-testid="quote-assessment-notes" rows={2} placeholder="What was inspected / found on the vehicle" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">WORK CATEGORY *</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} required data-testid="quote-category" className="w-full rounded-lg border border-input px-3.5 py-3 text-base outline-none focus:border-brand">
              <option value="">Select category…</option>
              {catGroups.map((g) => (
                <optgroup key={g.id || g.label} label={g.label || g.name}>
                  {(g.categories || g.items || []).map((c) => <option key={c.id} value={c.id}>{c.label || c.name}</option>)}
                </optgroup>
              ))}
            </select></label>
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">VALID UNTIL</span>
            <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="quote-valid" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
          <label className="block"><span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">MILEAGE</span>
            <input type="number" value={mileage} onChange={(e) => setMileage(e.target.value)} data-testid="quote-mileage" placeholder="e.g. 54210" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" /></label>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between"><span className="text-[11px] font-bold tracking-widest text-muted-foreground">LINE ITEMS</span>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setPickerOpen(true)} data-testid="quote-new-add-from-stock" className="inline-flex items-center gap-1 text-sm font-bold text-brand-2"><Package className="h-4 w-4" />From stock</button>
              <button type="button" onClick={() => setItems([...items, { description: "", qty: 1, unit_price: "", kind: "labour" }])} className="inline-flex items-center gap-1 text-sm font-bold text-brand-2"><Plus className="h-4 w-4" />Add</button>
            </div></div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex gap-2" data-testid={`quote-item-${i}`}>
                <input value={it.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Description" className="min-w-0 flex-1 rounded-md border border-input px-2.5 py-2 text-sm outline-none focus:border-brand" />
                <select value={it.kind} onChange={(e) => upd(i, "kind", e.target.value)} className="rounded-md border border-input px-1 py-2 text-xs outline-none"><option value="labour">Labour</option><option value="part">Part</option></select>
                <input type="number" value={it.qty} onChange={(e) => upd(i, "qty", e.target.value)} className="w-14 rounded-md border border-input px-2 py-2 text-center text-sm outline-none focus:border-brand" />
                <input type="number" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className="w-20 rounded-md border border-input px-2 py-2 text-right text-sm outline-none focus:border-brand" />
                <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="text-danger"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-6 border-t border-border pt-3 text-sm">
          <span className="text-muted-foreground">Net {money(net)}</span>
          {vatPct > 0 && <span className="text-muted-foreground">VAT {money(vat)}</span>}
          <span className="font-display text-lg font-bold text-midnight">{money(net + vat)}</span>
        </div>

        <div>
          <span className="mb-1.5 block text-[11px] font-bold tracking-widest text-muted-foreground">IMPORTANT NOTE</span>
          <textarea value={importantNote} onChange={(e) => setImportantNote(e.target.value)} data-testid="quote-important-note" rows={2} placeholder="Anything the customer must know (e.g. price confirmed on inspection)" className="w-full rounded-lg border border-input px-3.5 py-3 text-sm outline-none focus:border-brand" />
        </div>

        <button type="submit" disabled={saving} data-testid="quote-save-btn" className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand py-3.5 text-sm font-extrabold tracking-wide text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "SAVE QUOTATION"}
        </button>
      </form>
      <PartPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={(p) => setItems((prev) => [...prev, { description: p.name, qty: 1, unit_price: Number(p.unit_price) || 0, kind: "part" }])} />
    </div>
  );
}
